/**
 * SEO configuration and structured-data helpers.
 */

import { getSiteUrl } from './environment';

const SITE_NAME = 'SolidlyAI Agents';
const DEFAULT_DESCRIPTION =
  'Turn your Claude subscription into a team of AI coworkers—organized in one workspace, each with its own permissions and skills. They sit in on your meetings (on your machine, your own keys) and pick up the work live. Buy once, own forever.';

export const getDefaultSEO = () => {
  const siteUrl = getSiteUrl();
  return {
    title: 'SolidlyAI Agents — A team of AI coworkers, built on Claude',
    description: DEFAULT_DESCRIPTION,
    keywords:
      'AI coworkers, AI agent team, agentic AI, Claude agents, Claude subscription, meeting notetaker, voiceprint speaker ID, agent permissions, agent skills, agent orchestration, BYOK',
    canonical: siteUrl,
    ogTitle: 'SolidlyAI Agents — A team of AI coworkers, built on Claude',
    ogDescription: DEFAULT_DESCRIPTION,
    ogImage: `${siteUrl}/og-image.jpg`,
    twitterImage: `${siteUrl}/twitter-image.jpg`,
  };
};

export const getOrganizationSchema = () => {
  const siteUrl = getSiteUrl();
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: siteUrl,
    logo: `${siteUrl}/logo512.png`,
  };
};

export const getSoftwareApplicationSchema = () => {
  const siteUrl = getSiteUrl();
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: SITE_NAME,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Windows, macOS',
    description: DEFAULT_DESCRIPTION,
    url: siteUrl,
    offers: {
      '@type': 'Offer',
      price: '25',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
  };
};

// Keep this list in sync with the on-page FAQ in components/Faq.js.
export const getFAQSchema = () => {
  const qa = [
    {
      q: 'What do I need to run it?',
      a: 'A Claude subscription (the agents run on your plan) and a transcription provider API key for meeting capture. You install the app on Windows or macOS and plug in your own keys.',
    },
    {
      q: 'Are there any ongoing costs to Solidly?',
      a: 'The app is a one-time purchase — buy it once and own it forever, including all future updates. The only subscription we offer is the optional Solidly Sync add-on ($5/month) for encrypted backup and multi-device sync, and the app is fully functional without it. Everything else you pay goes straight to your own providers (Claude and your transcription key) for what you use.',
    },
    {
      q: 'Is my meeting data private?',
      a: 'Meetings are captured on your machine — no bot joins the call. Transcription runs on your own provider key and agents run on your own Claude subscription. By default your workspace stays on your device; if you enable the optional Solidly Sync add-on, your data is encrypted before it ever leaves your machine.',
    },
    {
      q: 'What is Solidly Sync?',
      a: 'An optional $5/month add-on for encrypted cloud backup and multi-device sync. Back up your whole workspace and restore it on another machine in minutes, with one primary device designated to run your scheduled agents so the same job never fires twice. Cancel anytime; the app keeps working fully on your device.',
    },
    {
      q: 'What powers the agents?',
      a: 'The agents are built on Claude and run on your own Claude subscription. You manage a team of scheduled and on-demand agents that coordinate and hand work off to each other.',
    },
    {
      q: 'Which operating systems are supported?',
      a: 'SolidlyAI Agents is a desktop app for Windows and macOS.',
    },
    {
      q: 'What is your refund policy?',
      a: 'A 7-day money-back guarantee. If it is not for you, email us within 7 days of purchase for a full refund.',
    },
    {
      q: 'Do I get updates?',
      a: 'Yes — every future update is included free, for the lifetime of the product. The app updates itself automatically, with no upgrade fees and no subscription required.',
    },
  ];

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: qa.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };
};
